import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@moneyskyline/ui';
import { HonestInline } from '@/components/HonestInline';
import type { Account } from '@/api/hooks/useAccounts';
import { useUpdateManualAccount } from './useUpdateManualAccount';

const BUSINESS_USE_OPTIONS = ['none', 'mixed', 'primary'] as const;

export function ManualAccountEditor({
  account,
  open,
  onOpenChange,
}: {
  account: Account;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const mutation = useUpdateManualAccount();
  const [displayName, setDisplayName] = useState(account.display_name);
  const [businessUse, setBusinessUse] = useState(account.business_use);
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (mutation.isError) nameInputRef.current?.focus();
  }, [mutation.isError]);

  if (account.source !== 'manual') return null;

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    mutation.mutate(
      {
        accountId: account.account_id,
        ...(displayName !== account.display_name ? { display_name: displayName } : {}),
        ...(businessUse !== account.business_use ? { business_use: businessUse } : {}),
      },
      { onSuccess: () => onOpenChange(false) },
    );
  }

  const hasChanges = displayName !== account.display_name || businessUse !== account.business_use;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="manual-account-editor">
        <DialogHeader>
          <DialogTitle>Edit account details</DialogTitle>
          <DialogDescription>Update the details MoneySkyline keeps for this manual account.</DialogDescription>
        </DialogHeader>
        <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
          <label className="flex flex-col gap-1 text-sm">
            Name
            <input
              ref={nameInputRef}
              autoFocus
              className="min-h-11 rounded-md border border-input px-3"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              required
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Business use
            <select
              className="min-h-11 rounded-md border border-input px-3"
              value={businessUse}
              onChange={(event) => setBusinessUse(event.target.value as Account['business_use'])}
            >
              {BUSINESS_USE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <fieldset disabled className="flex flex-col gap-3 rounded-md border border-border p-3">
            <legend className="px-1 text-sm">Unavailable account details</legend>
            <label className="flex flex-col gap-1 text-sm">
              Account type
              <select value={account.kind} onChange={() => undefined}>
                <option value={account.kind}>{account.kind}</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Currency
              <input value={account.currency} onChange={() => undefined} />
            </label>
            <p className="text-sm text-muted-foreground">
              Account type, currency, and versioned changes aren’t available yet.
            </p>
          </fieldset>
          {mutation.isError && (
            <>
              <HonestInline kind="api-unavailable" />
              <p role="alert" className="text-sm text-negative-foreground">
                We couldn’t confirm those account details. Your changes are still here.
              </p>
            </>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending || !hasChanges}>
              {mutation.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default ManualAccountEditor;
